import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import { SideBar } from '../../layout';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import DynamicSnackbar from '../../common/Snackbar';
import JobService from '../../../services/JobsService';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HomeIcon from '@mui/icons-material/Home';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { formatDistanceToNow, format } from 'date-fns';

const HRJobStatus = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [selectedJob, setSelectedJob] = useState(null);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [runImmediately, setRunImmediately] = useState(true);
    const [refreshInterval, setRefreshInterval] = useState(null);
    const [celeryStatus, setCeleryStatus] = useState({
        isChecking: false,
        isRunning: false,
        workers: [],
        lastChecked: null,
        message: 'Not checked yet'
    });

    // Fetch all jobs
    const fetchJobs = async () => {
        try {
            const response = await JobService.fetchJobStatus();
            setJobs(response);
            setLoading(false);
            return response;
        } catch (error) {
            console.error('Error fetching jobs:', error);
            setSnackbarMessage('Failed to fetch jobs');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setLoading(false);
            return [];
        }
    };

    // Fetch a specific job's status
    const fetchJobStatus = async (jobId) => {
        try {
            const response = await JobService.getJobStatus(jobId);
            
            // Update the job in the jobs list
            setJobs(prevJobs => 
                prevJobs.map(job => 
                    job.id === jobId ? { ...job, ...response } : job
                )
            );
            
            // If this is the selected job, update it too
            if (selectedJob && selectedJob.id === jobId) {
                setSelectedJob(prev => ({ ...prev, ...response }));
            }
            
            return response;
        } catch (error) {
            console.error(`Error fetching job status for ${jobId}:`, error);
            return null;
        }
    };

    // Initial data fetch
    useEffect(() => {
        // Fetch jobs and select first job
        fetchJobs().then(() => {
            // Select the first job by default if jobs are loaded and no job is selected
            if (jobs.length > 0 && !selectedJob) {
                setSelectedJob(jobs[0]);
            }
        });
        
        // Check Celery status
        checkCeleryStatus();
        
        // Set up refresh interval (every 30 seconds)
        const interval = setInterval(() => {
            if (jobs.length > 0) {
                jobs.forEach(job => fetchJobStatus(job.id));
            }
            // Check Celery status every 30 seconds as well
            checkCeleryStatus();
        }, 30000);
        
        setRefreshInterval(interval);
        
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [jobs.length]);

    const handleRefresh = () => {
        fetchJobs();
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const checkCeleryStatus = async (startIfNotRunning = false) => {
        try {
            setCeleryStatus(prev => ({ ...prev, isChecking: true }));
            const response = await JobService.checkCeleryStatus();
            
            const isRunning = response.celery_running;
            
            // If not running and startIfNotRunning flag is true, try to start Celery
            if (!isRunning && startIfNotRunning) {
                try {
                    setSnackbarMessage("Celery is not running. Attempting to start it...");
                    setSnackbarSeverity("info");
                    setSnackbarOpen(true);
                    
                    const startResponse = await JobService.startCelery();
                    
                    if (startResponse.celery_running) {
                        setCeleryStatus({
                            isChecking: false,
                            isRunning: true,
                            workers: startResponse.workers || [],
                            lastChecked: new Date(),
                            message: "Celery started successfully"
                        });
                        
                        setSnackbarMessage("Celery started successfully");
                        setSnackbarSeverity("success");
                        setSnackbarOpen(true);
                        return;
                    } else {
                        setSnackbarMessage(`Failed to start Celery: ${startResponse.message}`);
                        setSnackbarSeverity("error");
                        setSnackbarOpen(true);
                    }
                } catch (error) {
                    console.error("Error starting Celery:", error);
                    setSnackbarMessage(`Error starting Celery: ${error.message}`);
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                }
            }
            
            setCeleryStatus({
                isChecking: false,
                isRunning: isRunning,
                workers: response.workers || [],
                lastChecked: new Date(),
                message: response.message || (isRunning 
                    ? `Celery is running with ${response.worker_count} worker(s)` 
                    : 'Celery is not running')
            });
        } catch (error) {
            console.error('Error checking Celery status:', error);
            setCeleryStatus({
                isChecking: false,
                isRunning: false,
                workers: [],
                lastChecked: new Date(),
                message: 'Error checking Celery status'
            });
            
            setSnackbarMessage(`Error checking Celery status: ${error.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleRowClick = (job) => {
        setSelectedJob(job);
    };

    const handleAction = async (action, jobId) => {
        setActionLoading(jobId);
        
        try {
            let response;
            
            switch (action) {
                case 'pause':
                    response = await JobService.pauseJob(jobId);
                    break;
                case 'resume':
                    response = await JobService.resumeJob(jobId);
                    break;
                case 'stop':
                    response = await JobService.stopJob(jobId);
                    break;
                case 'run':
                    // For run, we might need the password, so open the dialog
                    setSelectedJob(jobs.find(job => job.id === jobId));
                    // Automatically set runImmediately based on Celery status
                    setRunImmediately(!celeryStatus.isRunning);
                    setPasswordDialogOpen(true);
                    setActionLoading(null);
                    return;
                default:
                    throw new Error(`Unknown action: ${action}`);
            }
            
            // Show success message
            setSnackbarMessage(response.message);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            
            // Refresh the job status
            await fetchJobStatus(jobId);
        } catch (error) {
            console.error(`Error performing ${action} on job ${jobId}:`, error);
            setSnackbarMessage(`Failed to ${action} job: ${error.response?.data?.message || error.message}`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setActionLoading(null);
        }
    };

    const handleRunWithPassword = async () => {
        if (!selectedJob) return;
        
        setActionLoading(selectedJob.id);
        setPasswordDialogOpen(false);
        
        try {
            const response = await JobService.runJob(selectedJob.id, password, runImmediately);
            
            // Show success message
            setSnackbarMessage(response.message);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            
            // Refresh the job status
            await fetchJobStatus(selectedJob.id);
        } catch (error) {
            console.error(`Error running job ${selectedJob.id}:`, error);
            setSnackbarMessage(`Failed to run job: ${error.response?.data?.message || error.message}`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setActionLoading(null);
            setPassword('');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE':
                return 'success.main';
            case 'PAUSED':
                return 'warning.main';
            case 'STOPPED':
                return 'error.main';
            case 'ERROR':
                return 'error.main';
            default:
                return 'text.secondary';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'ACTIVE':
                return <PlayCircleFilledIcon color="success" />;
            case 'PAUSED':
                return <PauseCircleFilledIcon color="warning" />;
            case 'STOPPED':
                return <StopCircleIcon color="error" />;
            case 'ERROR':
                return <ErrorIcon color="error" />;
            default:
                return <HourglassEmptyIcon color="disabled" />;
        }
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return 'Never';
        
        try {
            const date = new Date(dateTime);
            return format(date, 'PPpp') + ` (${formatDistanceToNow(date, { addSuffix: true })})`;
        } catch (error) {
            return dateTime;
        }
    };

    const getScheduleDays = (job) => {
        if (!job || !job.schedule) return (
            <mui.Typography variant="body2" color="text.secondary">
                Not scheduled
            </mui.Typography>
        );
        
        const days = [];
        if (job.schedule.monday) days.push('Mon');
        if (job.schedule.tuesday) days.push('Tue');
        if (job.schedule.wednesday) days.push('Wed');
        if (job.schedule.thursday) days.push('Thu');
        if (job.schedule.friday) days.push('Fri');
        if (job.schedule.saturday) days.push('Sat');
        if (job.schedule.sunday) days.push('Sun');
        
        if (days.length === 0) return (
            <mui.Typography variant="body2" color="text.secondary">
                Not scheduled
            </mui.Typography>
        );
        
        return (
            <mui.Box>
                <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                    {days.map((day) => (
                        <mui.Chip 
                            key={day} 
                            label={day} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ minWidth: '40px' }}
                        />
                    ))}
                </mui.Box>
                {job.schedule.time && (
                    <mui.Chip
                        icon={<AccessTimeIcon fontSize="small" />}
                        label={job.schedule.time}
                        size="small"
                        color="secondary"
                        sx={{ mt: 0.5 }}
                    />
                )}
            </mui.Box>
        );
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Breadcrumbs aria-label="breadcrumb">
                    <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                        <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                        Home
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        Dashboard
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/hr-data-management">
                        HR Data
                    </mui.Link>
                    <mui.Typography color="text.primary">Job Status</mui.Typography>
                </mui.Breadcrumbs>

                <SearchAppBar title="HR Job Status & Control" icon={<ScheduleIcon />} />
                <mui.Typography sx={{ marginTop: '20px' }} variant="subtitle2" gutterBottom>
                    Monitor and control your HR data import jobs
                </mui.Typography>

                <Separator />

                <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <mui.Tooltip 
                            title={
                                celeryStatus.isChecking 
                                    ? 'Checking Celery status...' 
                                    : celeryStatus.isRunning
                                        ? celeryStatus.message
                                        : "Click to start Celery"
                            }
                        >
                            <mui.Chip
                                icon={celeryStatus.isChecking ? <mui.CircularProgress size={16} /> : 
                                    celeryStatus.isRunning ? <CloudDoneIcon /> : <CloudOffIcon />}
                                label={
                                    celeryStatus.isChecking 
                                        ? 'Checking Celery' 
                                        : celeryStatus.isRunning 
                                            ? 'Celery Running' 
                                            : 'Start Celery'
                                }
                                color={
                                    celeryStatus.isChecking 
                                        ? 'default' 
                                        : celeryStatus.isRunning 
                                            ? 'success' 
                                            : 'error'
                                }
                                onClick={() => checkCeleryStatus(true)}
                                sx={{ 
                                    cursor: 'pointer',
                                    '&:hover': {
                                        boxShadow: celeryStatus.isRunning ? 'none' : '0 2px 4px rgba(0,0,0,0.2)',
                                        backgroundColor: celeryStatus.isRunning ? '' : '#f44336',
                                        '& .MuiChip-label': {
                                            color: celeryStatus.isRunning ? '' : 'white'
                                        },
                                        '& .MuiChip-icon': {
                                            color: celeryStatus.isRunning ? '' : 'white'
                                        }
                                    }
                                }}
                            />
                        </mui.Tooltip>
                        {celeryStatus.lastChecked && (
                            <mui.Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                Last checked: {formatDistanceToNow(celeryStatus.lastChecked, { addSuffix: true })}
                            </mui.Typography>
                        )}
                    </mui.Box>
                    <mui.Button 
                        variant="outlined" 
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        Refresh Status
                    </mui.Button>
                </mui.Box>

                {loading ? (
                    <mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <mui.CircularProgress />
                    </mui.Box>
                ) : jobs.length === 0 ? (
                    <mui.Paper sx={{ p: 3, textAlign: 'center' }}>
                        <mui.Typography variant="h6">No HR jobs found</mui.Typography>
                        <mui.Typography variant="body2" sx={{ mt: 1 }}>
                            Create a job schedule first to see status here
                        </mui.Typography>
                        <mui.Button 
                            variant="contained" 
                            color="primary" 
                            sx={{ mt: 2 }}
                            onClick={() => navigate('/interfaces/hr/jobs')}
                        >
                            Create Job Schedule
                        </mui.Button>
                    </mui.Paper>
                ) : (
                    <mui.TableContainer component={mui.Paper}>
                        <mui.Table>
                            <mui.TableHead>
                                <mui.TableRow>
                                    <mui.TableCell>Job Name</mui.TableCell>
                                    <mui.TableCell>Company</mui.TableCell>
                                    <mui.TableCell sx={{ minWidth: '220px' }}>Schedule</mui.TableCell>
                                    <mui.TableCell>Status</mui.TableCell>
                                    <mui.TableCell sx={{ minWidth: '160px' }}>Last Run</mui.TableCell>
                                    <mui.TableCell sx={{ minWidth: '160px' }}>Next Run</mui.TableCell>
                                    <mui.TableCell>Actions</mui.TableCell>
                                </mui.TableRow>
                            </mui.TableHead>
                            <mui.TableBody>
                                {jobs.map((job) => (
                                    <mui.TableRow 
                                        key={job.id} 
                                        onClick={() => handleRowClick(job)} 
                                        sx={{ 
                                            cursor: 'pointer',
                                            '&:hover': { 
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)' 
                                            },
                                            backgroundColor: selectedJob && selectedJob.id === job.id ? 'rgba(0, 0, 0, 0.08)' : 'inherit'
                                        }}
                                    >
                                        <mui.TableCell>{job.job_name}</mui.TableCell>
                                        <mui.TableCell>{job.company_name}</mui.TableCell>
                                        <mui.TableCell>{getScheduleDays(job)}</mui.TableCell>
                                        <mui.TableCell>
                                            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {getStatusIcon(job.status)}
                                                <mui.Typography 
                                                    sx={{ ml: 1, color: getStatusColor(job.status) }}
                                                >
                                                    {job.status}
                                                </mui.Typography>
                                            </mui.Box>
                                        </mui.TableCell>
                                        <mui.TableCell>
                                            <mui.Tooltip title={job.last_run ? formatDateTime(job.last_run) : 'Never'}>
                                                <mui.Box>
                                                    {job.last_run ? (
                                                        <>
                                                            <mui.Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                {format(new Date(job.last_run), 'PPP')}
                                                            </mui.Typography>
                                                            <mui.Chip
                                                                icon={<AccessTimeIcon fontSize="small" />}
                                                                label={format(new Date(job.last_run), 'p')}
                                                                size="small"
                                                                color="info"
                                                                sx={{ mt: 0.5 }}
                                                            />
                                                        </>
                                                    ) : (
                                                        <mui.Typography variant="body2" color="text.secondary">
                                                            Never
                                                        </mui.Typography>
                                                    )}
                                                </mui.Box>
                                            </mui.Tooltip>
                                        </mui.TableCell>
                                        <mui.TableCell>
                                            <mui.Tooltip title={job.next_run ? formatDateTime(job.next_run) : 'Not scheduled'}>
                                                <mui.Box>
                                                    {job.next_run ? (
                                                        <>
                                                            <mui.Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                {format(new Date(job.next_run), 'PPP')}
                                                            </mui.Typography>
                                                            <mui.Chip
                                                                icon={<AccessTimeIcon fontSize="small" />}
                                                                label={format(new Date(job.next_run), 'p')}
                                                                size="small"
                                                                color="info"
                                                                sx={{ mt: 0.5 }}
                                                            />
                                                        </>
                                                    ) : (
                                                        <mui.Typography variant="body2" color="text.secondary">
                                                            Not scheduled
                                                        </mui.Typography>
                                                    )}
                                                </mui.Box>
                                            </mui.Tooltip>
                                        </mui.TableCell>
                                        <mui.TableCell>
                                            <mui.ButtonGroup size="small" variant="outlined">
                                                {job.status === 'ACTIVE' ? (
                                                    <mui.Tooltip title="Pause Job">
                                                        <mui.Button
                                                            color="warning"
                                                            onClick={(e) => { e.stopPropagation(); handleAction('pause', job.id); }}
                                                            disabled={actionLoading === job.id}
                                                        >
                                                            {actionLoading === job.id ? (
                                                                <mui.CircularProgress size={20} />
                                                            ) : (
                                                                <PauseCircleFilledIcon />
                                                            )}
                                                        </mui.Button>
                                                    </mui.Tooltip>
                                                ) : job.status === 'PAUSED' || job.status === 'STOPPED' ? (
                                                    <mui.Tooltip title="Resume Job">
                                                        <mui.Button
                                                            color="success"
                                                            onClick={(e) => { e.stopPropagation(); handleAction('resume', job.id); }}
                                                            disabled={actionLoading === job.id}
                                                        >
                                                            {actionLoading === job.id ? (
                                                                <mui.CircularProgress size={20} />
                                                            ) : (
                                                                <PlayCircleFilledIcon />
                                                            )}
                                                        </mui.Button>
                                                    </mui.Tooltip>
                                                ) : null}
                                                
                                                {/* Show Run Now button for all jobs, including STOPPED jobs */}
                                                <mui.Tooltip title="Run Now">
                                                    <mui.Button
                                                        color="primary"
                                                        onClick={(e) => { e.stopPropagation(); handleAction('run', job.id); }}
                                                        disabled={actionLoading === job.id}
                                                    >
                                                        {actionLoading === job.id ? (
                                                            <mui.CircularProgress size={20} />
                                                        ) : (
                                                            <PlayCircleFilledIcon />
                                                        )}
                                                    </mui.Button>
                                                </mui.Tooltip>
                                                
                                                {job.status !== 'STOPPED' && (
                                                    <mui.Tooltip title="Stop Job">
                                                        <mui.Button
                                                            color="error"
                                                            onClick={(e) => { e.stopPropagation(); handleAction('stop', job.id); }}
                                                            disabled={actionLoading === job.id}
                                                        >
                                                            {actionLoading === job.id ? (
                                                                <mui.CircularProgress size={20} />
                                                            ) : (
                                                                <StopCircleIcon />
                                                            )}
                                                        </mui.Button>
                                                    </mui.Tooltip>
                                                )}
                                            </mui.ButtonGroup>
                                        </mui.TableCell>
                                    </mui.TableRow>
                                ))}
                            </mui.TableBody>
                        </mui.Table>
                    </mui.TableContainer>
                )}

                {/* Job Details Section - Show for selected job with details */}
                {selectedJob && (
                    <mui.Paper elevation={3} sx={{ p: 3, mt: 3 }}>
                        <mui.Typography variant="h6" gutterBottom>
                            Last Run Details: {selectedJob.job_name}
                        </mui.Typography>
                        
                        <mui.Grid container spacing={2}>
                            <mui.Grid item xs={12} sm={6} md={4}>
                                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <mui.Typography variant="body2">
                                        <strong>Run Time:</strong> {selectedJob.last_run ? formatDateTime(selectedJob.last_run) : 'Never run'}
                                    </mui.Typography>
                                </mui.Box>
                            </mui.Grid>
                            
                            {selectedJob.last_log && (
                                <>
                                    <mui.Grid item xs={12} sm={6} md={4}>
                                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                                            <mui.Typography variant="body2">
                                                <strong>File:</strong> {selectedJob.last_log.file_name}
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Grid>
                                    
                                    <mui.Grid item xs={12} sm={6} md={4}>
                                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CheckCircleIcon sx={{ mr: 1, color: selectedJob.last_log.complete ? 'success.main' : 'error.main' }} />
                                            <mui.Typography variant="body2">
                                                <strong>Status:</strong> {selectedJob.last_log.complete ? 'Complete' : 'Incomplete'}
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Grid>
                                    
                                    <mui.Grid item xs={12} sm={6} md={6}>
                                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <mui.Typography variant="body2">
                                                <strong>Source Count:</strong> {selectedJob.last_log.source_count}
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Grid>
                                    
                                    <mui.Grid item xs={12} sm={6} md={6}>
                                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <mui.Typography variant="body2">
                                                <strong>Processed Count:</strong> {selectedJob.last_log.dest_count}
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Grid>
                                </>
                            )}
                            
                            {!selectedJob.last_log && (
                                <mui.Grid item xs={12}>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            No execution details available. Run the job to see processing information.
                                        </mui.Typography>
                                    </mui.Box>
                                </mui.Grid>
                            )}
                            
                            {selectedJob.error_message && (
                                <mui.Grid item xs={12}>
                                    <mui.Alert severity="error">
                                        <mui.AlertTitle>Error</mui.AlertTitle>
                                        {selectedJob.error_message}
                                    </mui.Alert>
                                </mui.Grid>
                            )}
                        </mui.Grid>
                    </mui.Paper>
                )}
            </ResponsiveContainer>

            {/* Password Dialog for Run Now action */}
            <mui.Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
                <mui.DialogTitle>Enter SFTP Password</mui.DialogTitle>
                <mui.DialogContent>
                    <mui.DialogContentText>
                        To run this job immediately, please enter the SFTP password for authentication.
                    </mui.DialogContentText>
                    <mui.TextField
                        autoFocus
                        margin="dense"
                        label="SFTP Password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <mui.Box sx={{ mt: 2 }}>
                        <mui.FormControlLabel
                            control={
                                <mui.Checkbox
                                    checked={runImmediately}
                                    onChange={(e) => setRunImmediately(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Run immediately (synchronously without Celery)"
                        />
                        {!celeryStatus.isRunning && (
                            <mui.Alert severity="warning" sx={{ mt: 1 }}>
                                <mui.AlertTitle>Celery Not Running</mui.AlertTitle>
                                Celery appears to be unavailable. It's recommended to use the immediate execution option.
                            </mui.Alert>
                        )}
                    </mui.Box>
                </mui.DialogContent>
                <mui.DialogActions>
                    <mui.Button onClick={() => setPasswordDialogOpen(false)}>Cancel</mui.Button>
                    <mui.Button onClick={handleRunWithPassword} variant="contained" color="primary">
                        Run Job
                    </mui.Button>
                </mui.DialogActions>
            </mui.Dialog>

            <DynamicSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleSnackbarClose}
            />
        </div>
    );

    return (
        <div>
            <SideBar mainContent={customMainContent} />
        </div>
    );
};

export default HRJobStatus; 